// src/app/components/admin/share-link-expiry-setting.tsx
//
// Platform section of Global Settings: how long report SHARE LINKS stay valid.
//
// A share link is a public URL — anyone holding the token can read the report,
// with no login. So the link has to expire, and how soon is a security policy
// each deployment sets for itself. The value is stored on the backend
// (app_config, key='report_share_link') and read when a link is minted, so a
// change takes effect on the next link with no deploy. Admin-only; the backend
// enforces 403 on write for non-admins.
//
// Changing this never moves links already handed out: it applies to the next
// link created or refreshed. Shortening the policy therefore cannot extend an
// outstanding link, and lengthening it cannot revive an expired one.

import { useEffect, useState } from "react";
import { Link2, Check, Loader2 } from "lucide-react";

import {
  fetchShareLinkSettings,
  updateShareLinkSettings,
  type ShareLinkSettings,
} from "@/app/services/reporting/insights-api-service";

const PRESETS = [7, 30, 90];

export function ShareLinkExpirySetting() {
  const [settings, setSettings] = useState<ShareLinkSettings | null>(null);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [savedAt, setSavedAt] = useState(0);

  useEffect(() => {
    let alive = true;
    fetchShareLinkSettings()
      .then((next) => {
        if (!alive) return;
        setSettings(next);
        setDraft(String(next.ttl_days));
      })
      .catch((e) => alive && setErr(String(e?.message || e)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  async function save(days: number) {
    if (saving || !settings || days === settings.ttl_days) return;
    setSaving(true);
    setErr("");
    try {
      const stored = await updateShareLinkSettings(days);
      setSettings(stored);
      setDraft(String(stored.ttl_days));
      setSavedAt(Date.now());
    } catch (e: any) {
      setErr(String(e?.message || e));
      // Put the input back to what is actually stored, so a rejected value
      // never sits in the box looking like it took.
      if (settings) setDraft(String(settings.ttl_days));
    } finally {
      setSaving(false);
    }
  }

  const min = settings?.min_ttl_days ?? 1;
  const max = settings?.max_ttl_days ?? 365;
  const parsed = Number.parseInt(draft, 10);
  const draftValid = Number.isFinite(parsed) && parsed >= min && parsed <= max;
  const dirty = !!settings && draftValid && parsed !== settings.ttl_days;
  const disabled = loading || saving;

  return (
    <div className="py-6">
      <div className="max-w-2xl">
        <h2 style={{ fontSize: "var(--text-lg)", fontWeight: "var(--font-weight-semibold)", color: "var(--foreground)" }}>
          Report share-link expiry
        </h2>
        <p style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)", marginTop: 4 }}>
          Shared reports open without a login, so every share link expires. This sets how long a new
          link stays valid. Links already sent keep the expiry they were given — a change applies to
          the next link created or refreshed.
        </p>

        {err && (
          <div
            className="mt-4 rounded-lg px-3 py-2"
            style={{ fontSize: "var(--text-sm)", color: "var(--error-text)", backgroundColor: "var(--error-bg)" }}
          >
            {err}
          </div>
        )}

        <div className="mt-5 flex flex-wrap items-center gap-2">
          {PRESETS.map((days) => {
            const isCurrent = settings?.ttl_days === days;
            return (
              <button
                key={days}
                type="button"
                disabled={disabled}
                onClick={() => save(days)}
                className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 transition-all"
                style={{
                  cursor: disabled ? "default" : "pointer",
                  fontSize: "var(--text-sm)",
                  fontWeight: "var(--font-weight-medium)",
                  borderColor: isCurrent ? "var(--alpha-teal)" : "var(--border)",
                  backgroundColor: isCurrent ? "var(--alpha-teal-10)" : "var(--card, transparent)",
                  color: isCurrent ? "var(--alpha-teal)" : "var(--foreground)",
                  boxShadow: isCurrent ? "0 0 0 1px var(--alpha-teal)" : "none",
                  opacity: loading ? 0.6 : 1,
                }}
              >
                {isCurrent && <Check className="h-3.5 w-3.5" />}
                {days} days
              </button>
            );
          })}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span
            className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg"
            style={{ backgroundColor: "var(--muted, #f1f5f9)", color: "var(--muted-foreground)" }}
          >
            <Link2 className="h-4.5 w-4.5" />
          </span>

          <input
            type="number"
            min={min}
            max={max}
            value={draft}
            disabled={disabled}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && dirty) save(parsed);
            }}
            aria-label="Share-link lifetime in days"
            className="w-24 rounded-lg border px-3 py-2"
            style={{
              fontSize: "var(--text-sm)",
              color: "var(--foreground)",
              backgroundColor: "var(--card, transparent)",
              borderColor: draft !== "" && !draftValid ? "var(--error-border)" : "var(--border)",
            }}
          />
          <span style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>
            days ({min}–{max})
          </span>

          <button
            type="button"
            disabled={disabled || !dirty}
            onClick={() => save(parsed)}
            className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 transition-all"
            style={{
              cursor: disabled || !dirty ? "default" : "pointer",
              fontSize: "var(--text-sm)",
              fontWeight: "var(--font-weight-medium)",
              color: dirty ? "#fff" : "var(--muted-foreground)",
              backgroundColor: dirty ? "var(--alpha-teal)" : "var(--muted, #f1f5f9)",
            }}
          >
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Save
          </button>
        </div>

        {savedAt > 0 && !saving && !err && settings && (
          <p className="mt-3" style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
            Saved. New share links expire {settings.ttl_days} days after they are created.
          </p>
        )}
      </div>
    </div>
  );
}

export default ShareLinkExpirySetting;
